import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import interiorLibraryAdminApi from "../services/interiorLibraryAdminApi";

import BoardPreview3D from "../components/board-editor/BoardPreview3D";
import BoardRectangleInspector from "../components/board-editor/BoardRectangleInspector";
import BoardPolygonInspector from "../components/board-editor/BoardPolygonInspector";
import BoardFrameInfillInspector from "../components/board-editor/BoardFrameInfillInspector";
import BoardSketchCanvas2D from "../components/board-editor/BoardSketchCanvas2D";
import DefinitionParametersInspector from "../components/definition-editor/DefinitionParametersInspector";
import ParameterManagerModal from "../components/definition-editor/ParameterManagerModal";
import { validateParameters } from "../utils/parameterSystem";

const DEFAULT_BOARD_PARAMETERS = {
  width: 600,
  height: 720,
  thickness: 18,

  frameWidth: 70,
  frameThickness: 22,
  infillThickness: 4,
  infillInset: 12,
};

function normalizeDefinition(item) {
  return {
    id: item.id,
    code: item.code,
    name: item.name,
    nodeType: item.node_type,
    category: item.category,
    description: item.description,
    status: item.status,
    defaultParameters: item.default_parameters || DEFAULT_BOARD_PARAMETERS,
    geometryType: item.geometry_type || "profile_extrude",
    geometryDefaults: item.geometry_defaults || { thickness: "thickness" },
    frontFaceAxis: item.front_face_axis || "+z",
    anchors: item.anchors || [],
    children: item.children || [],
    profile: item.profile || {
      type: "rect",
      width_expr: "width",
      height_expr: "height",
    },
    sketchPlane: item.sketch_plane || "xy",
  };
}

function TabButton({ active, children, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-2 text-xs font-medium border-b-2 whitespace-nowrap ${
        active
          ? "border-blue-600 text-blue-700 bg-blue-50"
          : "border-transparent text-gray-500 hover:text-gray-800"
      }`}
    >
      {children}
    </button>
  );
}

function HeaderButton({ children, onClick, danger }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-2 rounded border bg-white ${
        danger ? "text-red-600" : ""
      }`}
    >
      {children}
    </button>
  );
}

export default function BoardDefinitionEditorPage() {
  const navigate = useNavigate();
  const { definitionId } = useParams();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [definition, setDefinition] = useState(null);

  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("board.panel");

  const [parameters, setParameters] = useState(DEFAULT_BOARD_PARAMETERS);
  const [profile, setProfile] = useState({
    type: "rect",
    width_expr: "width",
    height_expr: "height",
  });

  const [activeInspectorTab, setActiveInspectorTab] = useState("general");
  const [viewMode, setViewMode] = useState("preview");
  const [selectedPointId, setSelectedPointId] = useState(null);
  const [showParameterManager, setShowParameterManager] = useState(false);

const [formValidation, setFormValidation] = useState({
  ok: true,
  errors: [],
});

  async function load() {
    try {
      setLoading(true);

      const res = await interiorLibraryAdminApi.getDefinition(definitionId);
      const item = normalizeDefinition(res.item);

      setDefinition(item);
      setName(item.name || "");
      setCode(item.code || "");
      setDescription(item.description || "");
      setCategory(item.category || "board.panel");
      setParameters({
        ...DEFAULT_BOARD_PARAMETERS,
        ...(item.defaultParameters || {}),
      });
      setProfile(
        item.profile || {
          type: "rect",
          width_expr: "width",
          height_expr: "height",
        }
      );
      setSelectedPointId(null);
    } catch (err) {
      console.error("load board definition error:", err);
      alert("Failed to load board definition.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, [definitionId]);

  const profileMode = profile?.type || "rect";

  const canUseSketch = useMemo(() => profileMode === "polygon", [profileMode]);

  async function handleSave() {
    if (!definition) return;

    const validation = validateParameters({
  project: {},
  type: parameters,
  bindings: {},
  constraints: {},
  instance: {},
  parent: {},
});

if (!validation.ok) {
  setFormValidation(validation);
  return;
}

setFormValidation({ ok: true, errors: [] });

    try {
      setSaving(true);

      await interiorLibraryAdminApi.updateDefinition(definition.id, {
        code,
        name,
        node_type: "element",
        category,
        description,
        status: definition.status || "draft",
        default_parameters: parameters,
        geometry_type: "profile_extrude",
        geometry_defaults: {
          thickness: "thickness",
        },
        front_face_axis: "+z",
        anchors: definition.anchors || [],
        children: definition.children || [],
        profile,
        sketch_plane: "xy",
      });

      await load();
    } catch (err) {
      console.error("save board definition error:", err);
      alert(err?.response?.data?.message || "Save failed.");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!definition) return;

    const confirmed = window.confirm(`Delete board "${name}"?`);
    if (!confirmed) return;

    try {
      await interiorLibraryAdminApi.deleteDefinition(definition.id);
      navigate("/interior-editor/library");
    } catch (err) {
      console.error("delete board definition error:", err);
      alert("Delete failed.");
    }
  }

  function switchToFramedPanel() {
  setProfile({
    type: "framed_panel",
    outerProfile: {
      type: "rect",
      width_expr: "width",
      height_expr: "height",
    },
    frame: {
      enabled: true,
      frameWidth: "frameWidth",
      thickness: "frameThickness",
      materialSlot: "frame",
    },
    infill: {
      enabled: true,
      inset: "frameWidth",
      thickness: "infillThickness",
      materialSlot: "infill",
      type: "glass",
    },
  });

  setParameters((prev) => ({
    ...prev,
    frameWidth: prev.frameWidth ?? 70,
    frameThickness: prev.frameThickness ?? 22,
    infillThickness: prev.infillThickness ?? 4,
    infillInset: prev.infillInset ?? 12,
  }));

  setSelectedPointId(null);
  setViewMode("preview");
}

  function switchToRect() {
    setProfile({
      type: "rect",
      width_expr: "width",
      height_expr: "height",
    });
    setSelectedPointId(null);
    setViewMode("preview");
  }

  function switchToPolygon() {
  setProfile({
    type: "polygon",
    points: [
      { x: 0, y: 0 },
      { x: "width", y: 0 },
      { x: "width", y: "height" },
      { x: 0, y: "height" },
    ],
    cutouts: [],
  });
  setSelectedPointId(null);
  setViewMode("sketch");
}



  if (loading) {
    return (
      <div className="h-[calc(100vh-80px)] flex items-center justify-center text-gray-500">
        Loading board editor...
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-80px)] overflow-hidden flex flex-col bg-slate-100">
      <div className="h-16 border-b bg-white px-4 flex items-center justify-between">
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-wide text-gray-500">
            Board Definition Editor
          </div>
          <div className="text-lg font-semibold truncate">
            {name || "Board"}
          </div>
          <div className="text-xs text-gray-500 truncate">
            element · {code} · {category}
          </div>
        </div>

        {!formValidation.ok && (
  <div className="bg-red-50 border-b border-red-300 px-4 py-2 text-xs text-red-700">
    {formValidation.errors.map((e, i) => (
      <div key={i}>
        <b>{e.key}</b>: {e.message} — {String(e.raw)}
      </div>
    ))}
  </div>
)}

        <div className="flex gap-2">
          <HeaderButton onClick={() => setShowParameterManager(true)}>
  Parameters
</HeaderButton>
          <HeaderButton onClick={() => navigate("/interior-editor/library")}>
            Library
          </HeaderButton>
          <HeaderButton onClick={() => navigate("/interior-editor/definitions")}>
            Definitions
          </HeaderButton>
          <HeaderButton onClick={() => navigate(-1)}>Back</HeaderButton>
          <HeaderButton onClick={handleDelete} danger>
            Delete
          </HeaderButton>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 rounded bg-green-600 text-white disabled:opacity-50"
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-hidden grid grid-cols-[340px_minmax(0,1fr)]">
        <aside className="min-h-0 border-r bg-white flex flex-col">
          <div className="border-b px-3 pt-3">
            <div className="font-semibold">Board Manager</div>
            <div className="text-xs text-gray-500 mb-2">
              Type parameters, profile and generated 3D geometry.
            </div>

            <div className="flex gap-1 overflow-x-auto">
              <TabButton
                active={activeInspectorTab === "general"}
                onClick={() => setActiveInspectorTab("general")}
              >
                General
              </TabButton>
              <TabButton
                active={activeInspectorTab === "params"}
                onClick={() => setActiveInspectorTab("params")}
              >
                Parameters
              </TabButton>
              <TabButton
                active={activeInspectorTab === "profile"}
                onClick={() => setActiveInspectorTab("profile")}
              >
                Profile
              </TabButton>
            </div>
          </div>

          <div className="flex-1 overflow-auto p-3">
            {activeInspectorTab === "general" && (
              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    Name
                  </label>
                  <input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full px-3 py-2 border rounded"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    Code
                  </label>
                  <input
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    className="w-full px-3 py-2 border rounded"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    Category
                  </label>
                  <input
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    list="board-category-presets"
                    className="w-full px-3 py-2 border rounded"
                  />
                  <datalist id="board-category-presets">
                    <option value="board.side" />
                    <option value="board.top" />
                    <option value="board.bottom" />
                    <option value="board.back" />
                    <option value="board.shelf" />
                    <option value="front.door" />
                    <option value="front.drawer" />
                  </datalist>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    Description
                  </label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    className="w-full px-3 py-2 border rounded min-h-[100px]"
                  />
                </div>
              </div>
            )}

            {activeInspectorTab === "params" && (
              <DefinitionParametersInspector
                parameters={parameters}
                onChange={setParameters}
                title="Board Type Parameters"
              />
            )}

            {activeInspectorTab === "profile" && (
              <div className="space-y-4">
                <div>
                  <div className="font-semibold mb-2">Profile Type</div>
                  <div className="grid grid-cols-3 gap-2">
  <button
    type="button"
    onClick={switchToRect}
    className={`px-3 py-2 rounded border ${
      profileMode === "rect"
        ? "bg-blue-600 text-white"
        : "bg-white"
    }`}
  >
    Rectangle
  </button>

  <button
    type="button"
    onClick={switchToPolygon}
    className={`px-3 py-2 rounded border ${
      profileMode === "polygon"
        ? "bg-blue-600 text-white"
        : "bg-white"
    }`}
  >
    Polygon
  </button>

  <button
    type="button"
    onClick={switchToFramedPanel}
    className={`px-3 py-2 rounded border ${
      profileMode === "framed_panel"
        ? "bg-blue-600 text-white"
        : "bg-white"
    }`}
  >
    Framed
  </button>
</div>
                </div>

                {profileMode === "rect" ? (
  <BoardRectangleInspector
    parameters={parameters}
    onChangeParameters={setParameters}
    profile={profile}
    onChangeProfile={setProfile}
  />
) : profileMode === "polygon" ? (
  <BoardPolygonInspector
    parameters={parameters}
    onChangeParameters={setParameters}
    profile={profile}
    onChangeProfile={setProfile}
  />
) : (
  <BoardFrameInfillInspector
    parameters={parameters}
    onChangeParameters={setParameters}
    profile={profile}
    onChangeProfile={setProfile}
  />
)}
              </div>
            )}
          </div>
        </aside>

        <main className="min-h-0 overflow-hidden flex flex-col bg-slate-100">
          <div className="h-12 border-b bg-white px-3 flex items-center justify-between">
            <div className="text-sm text-gray-600">
              {profileMode === "polygon"
                ? "Sketch and 3D preview"
                : "3D preview"}
            </div>

            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setViewMode("sketch")}
                disabled={!canUseSketch}
                className={`px-3 py-1.5 rounded border text-sm ${
                  viewMode === "sketch"
                    ? "bg-blue-600 text-white"
                    : "bg-white"
                } disabled:opacity-40`}
              >
                Sketch
              </button>
              <button
                type="button"
                onClick={() => setViewMode("preview")}
                className={`px-3 py-1.5 rounded border text-sm ${
                  viewMode === "preview"
                    ? "bg-blue-600 text-white"
                    : "bg-white"
                }`}
              >
                3D Preview
              </button>
            </div>
          </div>

          <div className="flex-1 min-h-0 overflow-hidden">
            {viewMode === "sketch" && profileMode === "polygon" ? (
              <BoardSketchCanvas2D
                profile={profile}
                parameters={parameters}
                onChangeProfile={setProfile}
                selectedPointId={selectedPointId}
                onSelectPoint={setSelectedPointId}
              />
            ) : (
              <BoardPreview3D profile={profile} parameters={parameters} selected />
            )}
          </div>
        </main>
      </div>

      {showParameterManager && (
  <ParameterManagerModal
  isOpen={showParameterManager}
  onClose={() => setShowParameterManager(false)}
  projectParameters={{}}
  onChangeProjectParameters={() => {}}
  typeParameters={parameters}
  onChangeTypeParameters={setParameters}
  resolvedNode={{
    name: name || "Board",
    effectiveParameters: parameters,
    resolvedParameters: Object.entries(parameters || {}).map(([key, value]) => ({
      key,
      value,
      source: "type",
    })),
    parameterDebug: {
      definitionDefaults: parameters,
      effectiveParameters: parameters,
    },
    node: null,
    definition: {
      id: definition?.id,
      name,
      code,
      nodeType: "element",
      category,
      defaultParameters: parameters,
    },
  }}
  onUpdateNode={() => {}}
/>
)}
    </div>
  );
}